<template>
    <div class="ocr-tool-container">
        <div class="ocr-tool-card">
            <!-- 标题 -->
            <h1 class="title">🔍 文字猎人</h1>
            <p class="subtitle">从图片或视频中提取文字内容，智能识别，一键复制</p>

            <!-- 上传区域 -->
            <div
                    class="upload-area"
                    @dragover.prevent
                    @dragenter="isDragging = true"
                    @dragleave="isDragging = false"
                    @drop="handleDrop"
                    :class="{ 'drag-over': isDragging }"
            >
                <div class="upload-content">
                    <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
                        <path d="M14 2v6h6" />
                        <circle cx="10" cy="14" r="2" />
                        <path d="M4 16l4-4 4 4" />
                    </svg>
                    <p class="upload-text">拖拽图片/视频到此处或点击上传</p>
                    <p class="upload-hint">支持 JPG, PNG, WEBP, MP4, WebM 等格式</p>
                    <button class="upload-button" @click="triggerFileInput">
                        选择文件
                    </button>
                    <input
                            type="file"
                            ref="fileInput"
                            accept="image/*,video/*"
                            @change="handleUpload"
                            class="file-input"
                    />
                </div>
            </div>

            <!-- 媒体预览 -->
            <div v-if="mediaUrl" class="section">
                <h2 class="section-title">🖼️ 原始内容</h2>
                <div class="media-preview">
                    <img v-if="isImage" :src="mediaUrl" alt="上传内容" class="preview-image" />
                    <video
                            v-else
                            ref="videoPlayer"
                            :src="mediaUrl"
                            controls
                            class="preview-video"
                            @loadedmetadata="onVideoLoaded"
                    ></video>
                </div>
            </div>

            <!-- 视频帧选择 -->
            <div v-if="isVideo && mediaUrl" class="section">
                <h2 class="section-title">⏱️ 选择识别帧</h2>
                <div class="frame-selector">
                    <input
                            type="range"
                            :min="0"
                            :max="videoDuration"
                            :step="0.1"
                            v-model.number="currentFrameTime"
                            class="frame-slider"
                    />
                    <div class="time-display">
                        当前帧: {{ formatTime(currentFrameTime) }} / {{ formatTime(videoDuration) }}
                    </div>
                </div>
            </div>

            <!-- 识别设置 -->
            <div v-if="mediaUrl" class="section settings">
                <h2 class="section-title">⚙️ 识别设置</h2>
                <div class="settings-grid">
                    <div class="setting-item">
                        <label>语言</label>
                        <select v-model="selectedLang">
                            <option value="eng">英语</option>
                            <option value="chi_sim">简体中文</option>
                            <option value="chi_tra">繁体中文</option>
                            <option value="jpn">日语</option>
                            <option value="kor">韩语</option>
                        </select>
                    </div>
                    <div class="setting-item">
                        <label>模式</label>
                        <select v-model="ocrMode">
                            <option value="single">当前帧/图片</option>
                            <option value="batch">视频多帧识别（实验）</option>
                        </select>
                    </div>
                </div>
            </div>

            <!-- 操作按钮 -->
            <div class="action-area">
                <button
                        @click="performOCR"
                        :disabled="isProcessing || !mediaUrl"
                        class="generate-btn"
                        :class="{ 'generating': isProcessing }"
                >
                    <span v-if="!isProcessing">🔤 识别文字</span>
                    <span v-else>🧠 识别中... {{ progress }}%</span>
                </button>

                <button @click="clearAll" class="clear-btn">
                    🗑️ 清空
                </button>
            </div>

            <!-- 识别结果 -->
            <div v-if="ocrResult" class="section result-section">
                <h2 class="section-title">📋 识别结果</h2>
                <div class="result-container">
          <textarea
                  v-model="ocrResult"
                  class="result-textarea"
                  placeholder="识别的文字将显示在这里..."
                  readonly
          ></textarea>
                    <div class="result-actions">
                        <button class="copy-btn" @click="copyResult">
                            📋 复制
                        </button>
                        <button class="clear-result-btn" @click="ocrResult = ''">
                            ❌ 清空
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>🔍 在线 OCR 文字识别工具</h2>
            <p>从图片或视频帧中提取文字内容，支持中文、英文、日文、韩文等多种语言。基于 Tesseract.js 引擎，识别准确率高，完全在浏览器本地运行。</p>

            <h3>✨ 功能特点</h3>
            <ul>
                <li><strong>多语言支持</strong>：中文、英文、日文、韩文等 100+ 语言</li>
                <li><strong>图片+视频</strong>：支持上传图片或从视频中提取帧</li>
                <li><strong>一键复制</strong>：识别结果一键复制到剪贴板</li>
                <li><strong>高准确率</strong>：印刷体识别率 95%+</li>
            </ul>

            <h3>📝 使用教程</h3>
            <ol>
                <li>选择识别类型：图片或视频</li>
                <li>上传文件或拖拽到上传区</li>
                <li>等待识别完成（根据图片大小需几秒到几十秒）</li>
                <li>查看识别结果，一键复制文字</li>
            </ol>

            <h3>💡 提高识别率技巧</h3>
            <p><strong>图片清晰</strong>：文字边缘清晰，无模糊<br>
            <strong>光线均匀</strong>：避免过曝或过暗<br>
            <strong>背景简单</strong>：纯色背景比复杂背景识别率高<br>
            <strong>文字方向</strong>：确保文字正向，非旋转状态</p>

            <h3>🔒 隐私说明</h3>
            <p>使用 Tesseract.js 在浏览器本地识别，图片/视频不会上传到服务器。</p>
        </div>
    </div>
</template>

<script>
    import seoMixin from '../js/seo-mixin';
    // 引入 Tesseract.js（需 npm install tesseract.js）
    import { createWorker } from 'tesseract.js';

    export default {
        name: 'OcrTool',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: '在线OCR文字识别工具 - FreeToolBox',
                seoDescription: '免费在线OCR文字识别工具，从图片或视频中提取文字。支持中文、英文、日文、韩文等多种语言，浏览器本地处理，保护隐私安全。',
                seoKeywords: 'ocr文字识别,图片识别文字,在线ocr,文字提取,图片转文字',
                mediaUrl: null,
                isImage: true,
                isDragging: false,
                isProcessing: false,
                progress: 0,
                ocrResult: '',
                selectedLang: 'chi_sim',
                ocrMode: 'single',
                currentFrameTime: 0,
                videoDuration: 0,
            };
        },
        computed: {
            isVideo() {
                return !this.isImage;
            },
        },
        methods: {
            triggerFileInput() {
                this.$refs.fileInput.click();
            },
            handleUpload(event) {
                const file = event.target.files[0];
                if (file) this.loadMedia(file);
            },
            handleDrop(event) {
                event.preventDefault();
                this.isDragging = false;
                const file = event.dataTransfer.files[0];
                if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
                    this.loadMedia(file);
                }
            },
            loadMedia(file) {
                // 释放旧资源
                if (this.mediaUrl) URL.revokeObjectURL(this.mediaUrl);
                this.mediaUrl = URL.createObjectURL(file);
                this.isImage = file.type.startsWith('image/');
                this.clearResult();
                this.currentFrameTime = 0;
                this.videoDuration = 0;
            },
            onVideoLoaded() {
                const video = this.$refs.videoPlayer;
                this.videoDuration = parseFloat(video.duration.toFixed(1));
                this.currentFrameTime = 0;
            },
            formatTime(seconds) {
                const date = new Date(seconds * 1000);
                const mm = String(date.getUTCMinutes()).padStart(2, '0');
                const ss = String(date.getUTCSeconds()).padStart(2, '0');
                const ms = Math.floor((seconds % 1) * 100);
                return `${mm}:${ss}.${String(ms).padStart(2, '0')}`;
            },
            clearAll() {
                if (this.mediaUrl) {
                    URL.revokeObjectURL(this.mediaUrl);
                    this.mediaUrl = null;
                }
                this.clearResult();
                this.currentFrameTime = 0;
                this.videoDuration = 0;
            },
            clearResult() {
                this.ocrResult = '';
            },
            async performOCR() {
                if (!this.mediaUrl || this.isProcessing) return;

                this.isProcessing = true;
                this.progress = 0;

                try {
                    const canvas = await this.createCanvasFromMedia();
                    const text = await this.runTesseract(canvas);
                    this.ocrResult = text.trim() || '⚠️ 未识别到文字内容';
                } catch (error) {
                    this.ocrResult = `❌ 识别失败：${error.message}`;
                } finally {
                    this.isProcessing = false;
                }
            },
            async createCanvasFromMedia() {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                let element;

                if (this.isImage) {
                    element = new Image();
                    element.src = this.mediaUrl;
                    await new Promise((resolve) => (element.onload = resolve));
                } else {
                    element = this.$refs.videoPlayer;
                    element.currentTime = this.currentFrameTime;
                    await new Promise((resolve) => (element.onseeked = resolve));
                }

                // 设置画布尺寸（限制最大 1080px）
                const maxWidth = 1080;
                const scale = maxWidth / Math.max(element.videoWidth || element.naturalWidth, element.videoHeight || element.naturalHeight);
                canvas.width = (element.videoWidth || element.naturalWidth) * scale;
                canvas.height = (element.videoHeight || element.naturalHeight) * scale;

                ctx.drawImage(element, 0, 0, canvas.width, canvas.height);
                return canvas;
            },
            async runTesseract(canvas) {
                const worker = await createWorker({
                    logger: (m) => {
                        if (m.status === 'recognizing text') {
                            this.progress = Math.round(m.progress * 100);
                        }
                    },
                });

                try {
                    await worker.load();
                    await worker.loadLanguage(this.selectedLang);
                    await worker.initialize(this.selectedLang);
                    const { data: { text } } = await worker.recognize(canvas);
                    return text;
                } finally {
                    await worker.terminate();
                }
            },
            async copyResult() {
                try {
                    await navigator.clipboard.writeText(this.ocrResult);
                    alert('✅ 文字已复制到剪贴板！');
                } catch (err) {
                    prompt('复制失败，请手动复制：', this.ocrResult);
                }
            },
        },
        beforeUnmount() {
            if (this.mediaUrl) URL.revokeObjectURL(this.mediaUrl);
        },
    };
</script>

<style scoped>
    .ocr-tool-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 100vh;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        padding: 20px;
    }

    .ocr-tool-card {
        background: white;
        border-radius: 16px;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        padding: 30px;
        max-width: 800px;
        width: 100%;
    }

    .title {
        text-align: center;
        color: #333;
        margin-bottom: 10px;
        font-size: 2rem;
    }

    .subtitle {
        text-align: center;
        color: #666;
        margin-bottom: 30px;
        font-size: 1.1rem;
    }

    .upload-area {
        border: 2px dashed #ddd;
        border-radius: 12px;
        padding: 40px 20px;
        text-align: center;
        background-color: #f9f9f9;
        transition: all 0.3s ease;
        margin-bottom: 20px;
        cursor: pointer;
    }

    .upload-area.drag-over {
        border-color: #409eff;
        background-color: #ecf5ff;
    }

    .upload-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
    }

    .upload-icon {
        width: 48px;
        height: 48px;
        stroke: #409eff;
        fill: none;
    }

    .upload-text {
        font-size: 1.1rem;
        color: #333;
        font-weight: 500;
    }

    .upload-hint {
        color: #999;
        font-size: 0.9rem;
    }

    .upload-button {
        margin-top: 10px;
        padding: 10px 20px;
        background: #409eff;
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 1rem;
    }

    .upload-button:hover {
        background: #337ecc;
    }

    .file-input {
        display: none;
    }

    .section {
        margin: 30px 0;
    }

    .section-title {
        color: #333;
        margin-bottom: 15px;
        font-size: 1.2rem;
    }

    .media-preview {
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .preview-image,
    .preview-video {
        max-width: 100%;
        max-height: 400px;
        display: block;
    }

    .preview-video {
        width: 100%;
        height: auto;
    }

    .frame-selector {
        background: #f5f5f5;
        padding: 15px;
        border-radius: 8px;
    }

    .frame-slider {
        width: 100%;
        margin: 10px 0;
    }

    .time-display {
        text-align: center;
        color: #555;
        font-size: 0.9rem;
    }

    .settings-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 15px;
    }

    .setting-item {
        display: flex;
        flex-direction: column;
    }

    .setting-item label {
        margin-bottom: 5px;
        color: #333;
        font-weight: 500;
    }

    .setting-item select {
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 1rem;
    }

    .action-area {
        display: flex;
        gap: 15px;
        flex-wrap: wrap;
        justify-content: center;
        margin: 30px 0;
    }

    .generate-btn,
    .clear-btn {
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 500;
        transition: all 0.2s ease;
    }

    .generate-btn {
        background: #67c23a;
        color: white;
    }

    .generate-btn:hover:not(:disabled) {
        background: #5daf34;
    }

    .generate-btn.generating {
        background: #909399;
        cursor: not-allowed;
    }

    .clear-btn {
        background: #909399;
        color: white;
    }

    .clear-btn:hover {
        background: #797d82;
    }

    .result-container {
        background: #f8f9fa;
        border-radius: 8px;
        padding: 20px;
    }

    .result-textarea {
        width: 100%;
        height: 150px;
        padding: 12px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-family: 'Courier New', monospace;
        font-size: 1rem;
        resize: vertical;
        background: white;
    }

    .result-actions {
        display: flex;
        gap: 10px;
        margin-top: 10px;
        justify-content: flex-end;
    }

    .copy-btn,
    .clear-result-btn {
        padding: 8px 16px;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.9rem;
    }

    .copy-btn {
        background: #409eff;
        color: white;
    }

    .copy-btn:hover {
        background: #337ecc;
    }

    .clear-result-btn {
        background: #e6a23c;
        color: white;
    }

    .clear-result-btn:hover {
        background: #cf9236;
    }
    /* 内容说明区样式 */
    .tool-content {
        max-width: 800px;
        margin: 40px auto;
        padding: 30px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: left;
    }
    .tool-content h2 {
        color: #333;
        font-size: 1.5em;
        margin-bottom: 15px;
        border-bottom: 2px solid #3f51b5;
        padding-bottom: 10px;
    }
    .tool-content h3 {
        color: #555;
        font-size: 1.2em;
        margin: 20px 0 10px;
    }
    .tool-content p, .tool-content li {
        color: #666;
        line-height: 1.8;
        margin-bottom: 10px;
    }
    .tool-content ul, .tool-content ol {
        padding-left: 20px;
        margin-bottom: 15px;
    }
</style>